import { suggestedArticles } from '@web-app/helpers/suggested-articles';
import { useCMSPage } from '@web-app/hooks/use-cms-page';
import type { InferGetServerSidePropsType } from 'next/types';

import { formatMetas, formatAlternates, mapSanityBreadcrumb } from '@core/sanity/utils';
import { ERouting, NewsArticleLdProps } from '@core/type';
import { DynamicSEOEntity } from '@core/type/api';
import { SanityBlogArticle, BlogArticleQueryResult } from '@core/type/sanity';

import { fetchSanityData } from '../../../helpers/make-server-props/helpers/sanity';
import {
  makeServerSideProps,
  isRedirect,
} from '../../../helpers/make-server-props/make-server-props';
import { ViewBlogArticle } from '../../../view';

type BlogArticleParams = {
  category: string;
  slug: string;
};

export type PageBlogArticleProps = InferGetServerSidePropsType<typeof getServerSideProps>;

const PageBlogArticle = (pageProps: PageBlogArticleProps) => {
  const suggestedArticles =
    pageProps && 'suggestedArticles' in pageProps ? pageProps.suggestedArticles : [];
  return useCMSPage({
    ...pageProps,
    ViewPage: (props) => <ViewBlogArticle {...props} suggestedArticles={suggestedArticles} />,
  });
};

const getServerSideProps = makeServerSideProps<
  Required<DynamicSEOEntity> & {
    cmsResult: SanityBlogArticle;
    pageId: string;
    suggestedArticles: BlogArticleQueryResult['articles'];
    article: NewsArticleLdProps;
  },
  BlogArticleParams,
  BlogArticleParams,
  ERouting.BLOG_ARTICLE
>(ERouting.BLOG_ARTICLE, async (ctx) => {
  const res = await fetchSanityData<BlogArticleQueryResult>(ctx);
  if (isRedirect(res)) {
    return res;
  }
  const { articles, ...cmsResult } = res.props;

  const params = ctx.props.params;
  const categoryPageSlug = cmsResult.body.category.pageSlug;
  if (
    categoryPageSlug.slug.current !== params.category &&
    !categoryPageSlug.history.includes(params.category)
  ) {
    throw new Error('Not Found');
  }

  return {
    props: {
      cmsResult: cmsResult,
      pageId: cmsResult._id,
      suggestedArticles: suggestedArticles(cmsResult, articles),

      metas: formatMetas(cmsResult.metadata),
      alternates: formatAlternates(cmsResult.alternates?.alt, ctx.keyRoute),
      breadcrumbs: mapSanityBreadcrumb(cmsResult.body.breadcrumbs),
      blogCategories: cmsResult.blogCategories,
      article: {
        title: cmsResult.title || null,
        description: cmsResult.subtitle || null,
        publishedDate: cmsResult.body.publishedAt || null,
        modifiedDate: cmsResult._updatedAt || null,
        image: cmsResult.body.image || null,
      },
    },
  };
});

export { getServerSideProps };

export default PageBlogArticle;
